import { useState } from "react";

interface DeliveryDownloaderProps {
  getDeliveryRequestDownloadUids: (params: {
    report_no: string;
    is_defect_items: boolean;
    delivery_date?: string | null;
    item_id?: number | null;
    split_id?: number | null;
  }) => Promise<any>;
  generateReportsPDFByUuid: (params: {
    uuid: string;
    permitNo: string;
  }) => Promise<any>;
}

export const useDeliveryDownloader = ({
  getDeliveryRequestDownloadUids,
  generateReportsPDFByUuid,
}: DeliveryDownloaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "in_progress" | "success" | "error" | "pending"
  >("idle");
  const [permitNo, setPermitNo] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadInitiated, setDownloadInitiated] = useState(false);

  const handleDeliveryDownload = async (
    purchaseOrderNumber: string,
    deliveryDate: string | null,
    isDefectItems: boolean = false,
    downloadType: string = "all",
    itemId: number | null = null,
    splitId: number | null = null,
    cancellation: boolean = false
  ) => {
    if (isDownloading || downloadInitiated) return;

    setIsDownloading(true);
    setDownloadInitiated(true);

    const effectiveDate = normalizeDeliveryDateValue(deliveryDate) || "";
    const formattedDate = formatDateForBadge(effectiveDate);
    let fileName = `${purchaseOrderNumber}_${formattedDate}`;

    switch (downloadType) {
      case "normal":
        fileName += "_NormalItems";
        break;
      case "defect":
        fileName += "_DefectItems";
        break;
      case "redelivery":
        fileName += "_RedeliveryItems";
        break;
      case "all":
      default:
        fileName += "_AllItems";
        break;
    }

    // Set modal state
    setIsOpen(true);
    setStatus("in_progress");
    setPermitNo(fileName);
    setMessage("Starting PDF generation...");
    setErrorMessage("");

    try {
      // Step 1: Get UUID for the delivery request
      const uuidResponse = await getDeliveryRequestDownloadUids({
        report_no: purchaseOrderNumber,
        is_defect_items: isDefectItems,
        delivery_date: effectiveDate,
        item_id: itemId,
        split_id: splitId,
        cancellation,
      } as any);

      if (!uuidResponse?.data?.uuid) {
        throw new Error("Failed to start PDF generation - no UUID received");
      }

      setMessage("Generating PDF, please wait...");

      // Step 2: Poll for PDF generation status
      let attempts = 0;
      const maxAttempts = 30;
      //@ts-ignore
      let result;

      while (attempts < maxAttempts) {
        result = await generateReportsPDFByUuid({
          uuid: uuidResponse.data.uuid,
          permitNo: fileName,
        })
          //@ts-ignore
          .unwrap();

        setStatus(result.status);
        setMessage(result.message);

        if (result.status === "success" && result.signed_url) {
          // Create a hidden anchor tag to trigger download
          const link = document.createElement("a");
          link.href = result.signed_url;
          link.download = `${fileName}.pdf`;
          link.style.display = "none";

          // Append to body and click programmatically
          document.body.appendChild(link);
          link.click();

          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            // Revoke the object URL to free memory
            URL.revokeObjectURL(link.href);
          }, 100);

          setStatus("success");
          setMessage("PDF download started successfully");

          setTimeout(() => {
            setIsOpen(false);
            setStatus("idle");
          }, 2000);
          return;
        } else if (result.status === "error") {
          throw new Error(result.message || "PDF generation failed");
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
        attempts++;
      }

      throw new Error("PDF generation timed out. Please try again later.");
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    } finally {
      setIsDownloading(false);
      setTimeout(() => setDownloadInitiated(false), 3000); // Reset after cooldown
    }
  };

  const closeDownloadModal = () => {
    setIsOpen(false);
    setStatus("idle");
    setErrorMessage("");
    setMessage("");
  };

  return {
    // Return individual props instead of a state object
    isOpen,
    status,
    permitNo,
    errorMessage,
    message,
    handleDeliveryDownload,
    isDownloading,
    closeDownloadModal,
  };
};

// Helper function to format date for badge
const formatDateForBadge = (dateString: string) => {
  if (!dateString) return "ALLDATES";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "ALLDATES";
  return date.toLocaleDateString("en-GB").replace(/\//g, "");
};

const normalizeDeliveryDateValue = (value: string | null | undefined) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const lowered = raw.toLowerCase();
  if (
    lowered === "undefined" ||
    lowered === "null" ||
    lowered === "no date specified"
  ) {
    return null;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return raw;
};
